import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { clearAllOnboardingV2Drafts } from '../onboarding-v2/draft'
import { Mail } from 'lucide-react'
import useAuthStore from '../../store/authStore'
import { IS_SUPABASE_CONFIGURED, PORQUE_NO_CONFIGURADO, supabase } from '../../lib/supabase'
import { isEmailValid } from '../../lib/emailValidation'
import PLButton from '../ui/PLButton'
import PLWordmark from '../ui/PLWordmark'

/**
 * AuthPagePL v1.1.4-hotfix — autoconfirm flow (mailer_autoconfirm: true en Supabase Dashboard).
 *
 * Signup: tras signUp() → upserta PL_ONBOARDING_DRAFT → navigate('/today') directamente.
 * No se muestra pantalla "Revisa tu email". La sesión está disponible inmediatamente.
 *
 * Fallback: si emailNotConfirmed en login → resend + muestra CheckEmailScreen.
 * (Sólo ocurre si el usuario creó cuenta antes de activar autoconfirm.)
 *
 * Login: maneja error "Email not confirmed" con inline CTA de reenvío.
 *
 * Modo demo: si Supabase no configurado → persistDemoProfile() + /hoy.
 */

/**
 * COLA:el-front-llama-placeholder-a-localhost · EL MODAL DECÍA LO QUE NO ERA.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * Orden del founder (2026-10-18): «El modal de modo demo dice **"front sin backend configurado"**,
 * nunca "alpha cerrada"».
 *
 * 🔴 Lo que decía: «PrimeLab está en fase alpha cerrada. Las cuentas completas se abren
 * próximamente». Y lo que pasaba de verdad era que **este front no tiene backend configurado** — en
 * el entorno de cata local, porque el cliente llamaba placeholder a `localhost`. A quien venía a
 * catar se le decía que el producto estaba cerrado, cuando lo que estaba mal era el arranque. Una
 * pantalla que explica un fallo de configuración como una decisión de producto manda a buscar la
 * causa al sitio equivocado — y encima es mentira.
 *
 * Ahora dice lo que es, y **por qué** lo es, con el motivo que calcula `supabase.js`.
 */
const DEMO_MODAL_TITLE = 'Front sin backend configurado'
const DEMO_MODAL_BODY =
  'Este front no tiene un backend al que hablar, así que no puede crear cuentas ni entrar. No es que PrimeLab esté cerrado: es que falta la configuración de este arranque. Lo que escribas vive solo en este dispositivo.'

/** Qué falta exactamente, en una línea, del motivo que publica el cliente. */
const DEMO_PORQUE = {
  falta_env:        'Faltan REACT_APP_SUPABASE_URL o REACT_APP_SUPABASE_ANON_KEY.',
  url_placeholder:  'La URL configurada es un hueco de ejemplo sin rellenar.',
  clave_sin_forma:  'La clave no tiene forma de clave de Supabase (ni JWT ni sb_publishable_…).',
  url_no_reconocida: 'La URL no es el Supabase hosted (https://…supabase.co) ni un backend local (http://127.0.0.1:…).',
}

/** En local, además, se dice cómo se arranca: quien cata no tiene que adivinarlo. */
const DEMO_COMO_SE_ARREGLA = 'En local: bash scripts/cata/arrancar.sh (ver docs/CATA_LOCAL.md).'

const inputStyle = (focused, hasError) => ({
  width: '100%',
  height: 56,
  padding: '0 16px',
  background: 'var(--pl-subtle)',
  border: `1px solid ${hasError ? 'var(--pl-danger)' : focused ? 'var(--pl-terracota)' : 'var(--border)'}`,
  borderRadius: 12,
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 16,
  outline: 'none',
  transition: 'border-color var(--duration-fast) var(--ease-out-pl), box-shadow var(--duration-fast) var(--ease-out-pl)',
  boxShadow: focused && !hasError ? '0 0 0 2px color-mix(in srgb, var(--pl-terracota) 25%, transparent)' : 'none',
})

function DemoModal({ open, onClose }) {
  if (!open) return null
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 24,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: 24,
          maxWidth: 360,
          width: '100%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0, marginBottom: 8, fontSize: 18, fontWeight: 500, color: 'var(--text)' }}>
          {DEMO_MODAL_TITLE}
        </h3>
        <p style={{ margin: 0, marginBottom: 20, fontSize: 14, lineHeight: 1.55, color: 'var(--text-muted)' }}>
          {DEMO_MODAL_BODY}
        </p>
        <p data-testid="demo-porque" style={{ margin: 0, marginBottom: 20, fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-muted)' }}>
          {DEMO_PORQUE[PORQUE_NO_CONFIGURADO] || DEMO_PORQUE.url_no_reconocida} {DEMO_COMO_SE_ARREGLA}
        </p>
        <PLButton fullWidth onClick={onClose} style={{ background: 'var(--pl-terracota)', color: 'var(--pl-cream)', height: 48, borderRadius: 12 }}>
          Entendido
        </PLButton>
      </motion.div>
    </motion.div>
  )
}

function persistDemoProfile() {
  try {
    const draft = localStorage.getItem('PL_ONBOARDING_DRAFT')
    if (draft) localStorage.setItem('PL_ONBOARDING_PROFILE', draft)
    localStorage.setItem('PL_DEMO_MODE', '1')
  } catch {}
}

/** Pantalla "Revisa tu email" post-signup. */
function CheckEmailScreen({ email, onResend, onChangeEmail, resendLoading, resendSent }) {
  return (
    <motion.div
      key="check-email"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        paddingTop: 48,
        width: '100%',
      }}
    >
      {/* Icono sobre */}
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: 20,
          background: 'color-mix(in srgb, var(--pl-terracota) 12%, var(--bg-soft))',
          border: '1.5px solid color-mix(in srgb, var(--pl-terracota) 30%, transparent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 24,
        }}
      >
        <Mail size={28} color="var(--pl-terracota)" strokeWidth={1.5} />
      </div>

      <h1
        style={{
          margin: 0,
          fontSize: 22,
          fontWeight: 500,
          color: 'var(--text)',
          letterSpacing: '-0.01em',
          lineHeight: 1.2,
        }}
      >
        Revisa tu email
      </h1>

      <p
        style={{
          margin: '12px 0 0 0',
          maxWidth: 320,
          fontSize: 14,
          lineHeight: 1.6,
          color: 'var(--text-soft)',
        }}
      >
        Te hemos enviado un enlace de confirmación a{' '}
        <span style={{ color: 'var(--text)', fontWeight: 500 }}>{email}</span>.
        {' '}Haz clic en él para activar tu cuenta.
      </p>

      <p
        style={{
          margin: '8px 0 0 0',
          maxWidth: 300,
          fontSize: 13,
          lineHeight: 1.55,
          color: 'var(--text-muted)',
        }}
      >
        Si no lo ves, revisa la carpeta de spam.
      </p>

      <div style={{ marginTop: 32, width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {resendSent ? (
          <div
            style={{
              padding: '12px 16px',
              background: 'color-mix(in srgb, var(--pl-terracota) 8%, var(--bg-soft))',
              border: '1px solid color-mix(in srgb, var(--pl-terracota) 25%, transparent)',
              borderRadius: 12,
              fontSize: 13,
              color: 'var(--text-muted)',
              textAlign: 'center',
            }}
          >
            Email reenviado. Revisa tu bandeja de entrada.
          </div>
        ) : (
          <PLButton
            fullWidth
            size="lg"
            onClick={onResend}
            loading={resendLoading}
            style={{
              background: 'transparent',
              border: '1.5px solid color-mix(in srgb, var(--pl-terracota) 40%, transparent)',
              color: 'var(--pl-terracota-700)',
              height: 52,
              borderRadius: 12,
            }}
          >
            Reenviar email
          </PLButton>
        )}

        <button
          type="button"
          onClick={onChangeEmail}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: 13,
            cursor: 'pointer',
            padding: '8px 0',
            fontFamily: 'var(--font-sans)',
          }}
        >
          Usar otro email
        </button>
      </div>
    </motion.div>
  )
}

// ─── A2: Email validation ─────────────────────────────────────────────────────
// La regla vive en src/lib/emailValidation.js — una sola forma, con su test.

export default function AuthPagePL() {
  const navigate = useNavigate()
  const location = useLocation()
  const { signIn, signUp } = useAuthStore()

  // Cinceladura founder 2026-05-28 · OPCIÓN A · respeta intent del CTA de landing:
  // "Empezar" → ?mode=signup (default Crear cuenta) · "Ya tengo cuenta" → ?mode=login
  const initialMode = (() => {
    try {
      const p = new URLSearchParams(location.search).get('mode')
      if (p === 'login' || p === 'signup' || p === 'reset') return p
    } catch (_) {}
    return 'signup'
  })()
  const [mode, setMode] = useState(initialMode) // 'signup' | 'login' | 'reset'

  // Sync mode if location.search changes (back/forward navigation)
  useEffect(() => {
    try {
      const p = new URLSearchParams(location.search).get('mode')
      if (p === 'login' || p === 'signup' || p === 'reset') setMode(p)
    } catch (_) {}
  }, [location.search])
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [focused, setFocused] = useState(null)
  const [demoModalOpen, setDemoModalOpen] = useState(false)
  const [resetSent, setResetSent] = useState(false)

  // Email confirmation flow
  const [signupPending, setSignupPending] = useState(false)
  const [pendingEmail, setPendingEmail] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSent, setResendSent] = useState(false)

  // "Email not confirmed" inline state
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false)

  // ── A2: Live email validation ──────────────────────────────────────────────
  const emailTouched = email.length > 4
  const emailFormatOk = isEmailValid(email)
  const showEmailError = emailTouched && !emailFormatOk && mode !== 'reset'

  // ── A3: Live password rules ────────────────────────────────────────────────
  const pwRules = {
    length:    password.length >= 8,
    hasDigit:  /\d/.test(password),
    hasLetter: /[a-zA-Z]/.test(password),
  }
  const pwValid = pwRules.length && pwRules.hasDigit && pwRules.hasLetter

  const handleResend = async () => {
    setResendLoading(true)
    try {
      await supabase.auth.resend({ type: 'signup', email: pendingEmail })
      setResendSent(true)
    } catch {
      // silencioso — el usuario ya tiene el mensaje de "revisa spam"
    } finally {
      setResendLoading(false)
    }
  }

  const handleChangeEmail = () => {
    setSignupPending(false)
    setPendingEmail('')
    setResendSent(false)
    setPassword('')
    setPasswordConfirm('')
    setError(null)
  }

  const handleResendFromLogin = async () => {
    if (!email) return
    try {
      await supabase.auth.resend({ type: 'signup', email })
      setEmailNotConfirmed(false)
      setSignupPending(true)
      setPendingEmail(email)
    } catch { /* silencioso */ }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setEmailNotConfirmed(false)

    // MODO DEMO
    if (!IS_SUPABASE_CONFIGURED) {
      if (mode === 'signup') {
        persistDemoProfile()
        // P3 V2 · post-signup va a onboarding V2.
        navigate('/onboarding/v2')
        return
      }
      setDemoModalOpen(true)
      return
    }

    // MODO REAL
    setLoading(true)
    try {
      if (mode === 'signup') {
        // A2: email format + domain
        if (!isEmailValid(email)) {
          throw new Error('Email inválido. Comprueba el formato o usa otro email.')
        }
        if (password !== passwordConfirm) {
          throw new Error('Las contraseñas no coinciden')
        }
        // A3: password rules
        if (!pwValid) {
          throw new Error('La contraseña necesita mínimo 8 caracteres, una letra y un número.')
        }
        await signUp(email, password, '')
        // Cinceladura OPCIÓN A (2026-05-28) · post-signup → onboarding sinfónico SIEMPRE.
        // mailer_autoconfirm: true · sesión disponible inmediatamente.
        // Legacy draft handling preservado: si el usuario YA tenía draft pre-existente
        // (raro: solo si llegó a /auth tras un onboarding legacy parcial) → upsert y
        // saltar a /today; en otro caso → onboarding sinfónico C.8 limpio.
        let hadLegacyDraft = false
        try {
          const draft = localStorage.getItem('PL_ONBOARDING_PROFILE') || localStorage.getItem('PL_ONBOARDING_DRAFT')
          if (draft) {
            const { data: sessData } = await supabase.auth.getSession()
            const uid = sessData?.session?.user?.id
            if (uid) {
              await supabase.from('user_profiles').upsert({ id: uid, ...JSON.parse(draft), onboarding_completed: true })
              await supabase.from('profiles').upsert({ id: uid, onboarding_completed: true })
              localStorage.setItem('PL_ONBOARDING_PROFILE', draft)
              localStorage.removeItem('PL_ONBOARDING_DRAFT')
              clearAllOnboardingV2Drafts() // QA-E2E bug#2: borrar el draft v2 REAL tras migrar
              hadLegacyDraft = true
            }
          }
        } catch { /* non-blocking */ }
        navigate(hadLegacyDraft ? '/today' : '/onboarding/v2')
      } else if (mode === 'login') {
        await signIn(email, password)
        // Cinceladura OPCIÓN A · post-login → LandingOrToday decide:
        // onboarding_completed → /today · sino → resume /onboarding/sinfonico.
        navigate('/')
      } else if (mode === 'reset') {
        const { error: rErr } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/reset`,
        })
        if (rErr) throw rErr
        setResetSent(true)
      }
    } catch (err) {
      const msg = err.message || 'Error de autenticación'
      if (msg.toLowerCase().includes('email not confirmed') || msg.toLowerCase().includes('email_not_confirmed')) {
        setEmailNotConfirmed(true)
      } else {
        setError(msg)
      }
    } finally {
      setLoading(false)
    }
  }

  // --- Render ---

  return (
    <div
      data-theme="light"
      style={{
        minHeight: '100vh',
        // Cinceladura OPCIÓN A · cream branded · NO formulario frío.
        // Umbral cálido alineado con landing · PWA standalone safe (explícito,
        // no depende de --bg que puede variar por theme override).
        background: 'var(--pl-cream)',
        colorScheme: 'light',
        color: 'var(--pl-ink)',
        fontFamily: 'var(--font-sans)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          <img src="/logo-mark.svg" alt="" width={56} height={56} aria-hidden />
          <div style={{ marginTop: 16 }}>
            <PLWordmark size="lg" style={{ color: 'var(--text)' }} />
          </div>
          {!signupPending && (
            <p
              style={{
                margin: '32px 0 0 0',
                maxWidth: 320,
                fontSize: 14,
                lineHeight: 1.55,
                color: 'var(--text-muted)',
                textAlign: 'center',
              }}
            >
              El conocimiento para vivir en tu mejor versión ya existe. Ahora tiene sistema.
            </p>
          )}
        </motion.div>

        <AnimatePresence mode="wait">
          {signupPending ? (
            <CheckEmailScreen
              key="check-email"
              email={pendingEmail}
              onResend={handleResend}
              onChangeEmail={handleChangeEmail}
              resendLoading={resendLoading}
              resendSent={resendSent}
            />
          ) : (
            <motion.div
              key="auth-form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ width: '100%' }}
            >
              {/* Tab switcher */}
              {mode !== 'reset' && (
                <div
                  role="tablist"
                  style={{
                    marginTop: 48,
                    display: 'flex',
                    gap: 32,
                    borderBottom: '1px solid var(--border)',
                    width: '100%',
                    justifyContent: 'center',
                  }}
                >
                  {[
                    { id: 'signup', label: 'Crear cuenta' },
                    { id: 'login', label: 'Entrar' },
                  ].map((t) => {
                    const isActive = mode === t.id
                    return (
                      <button
                        key={t.id}
                        role="tab"
                        type="button"
                        onClick={() => { setMode(t.id); setError(null); setResetSent(false); setEmailNotConfirmed(false) }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: '12px 4px',
                          fontFamily: 'var(--font-sans)',
                          fontSize: 14,
                          fontWeight: 500,
                          color: isActive ? 'var(--pl-terracota)' : 'var(--text-muted)',
                          cursor: 'pointer',
                          borderBottom: `2px solid ${isActive ? 'var(--pl-terracota)' : 'transparent'}`,
                          marginBottom: -1,
                          transition: 'color var(--duration-fast) var(--ease-out-pl)',
                        }}
                      >
                        {t.label}
                      </button>
                    )
                  })}
                </div>
              )}

              {/* A1: noValidate suprime el validador nativo del browser (sale en idioma del SO,
                  no en español). La validación propia con copy de marca ya está en handleSubmit. */}
              <motion.form
                noValidate
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                onSubmit={handleSubmit}
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  marginTop: 24,
                  gap: 12,
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={mode}
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={{ duration: 0.2 }}
                    style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                  >
                    {mode === 'reset' && resetSent ? (
                      <div
                        style={{
                          padding: 20,
                          background: 'var(--bg-soft)',
                          border: '1px solid var(--border)',
                          borderRadius: 12,
                          textAlign: 'center',
                        }}
                      >
                        <p style={{ margin: 0, fontSize: 14, color: 'var(--text)' }}>
                          Te hemos enviado un email con instrucciones para recuperar tu contraseña.
                        </p>
                      </div>
                    ) : (
                      <>
                        <input
                          type="email"
                          autoComplete="email"
                          placeholder="Email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onFocus={() => setFocused('email')}
                          onBlur={() => setFocused(null)}
                          style={inputStyle(focused === 'email', showEmailError)}
                          required
                          data-testid="auth-email-input"
                        />
                        {/* A2: live email error */}
                        {showEmailError && (
                          <p style={{ margin: '-4px 0 0', fontSize: 12, color: 'var(--pl-danger)',
                            fontFamily: 'var(--font-sans)' }}
                            data-testid="auth-email-error">
                            Email inválido o dominio no permitido.
                          </p>
                        )}
                        {mode !== 'reset' && (
                          <input
                            type="password"
                            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                            placeholder="Contraseña"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onFocus={() => setFocused('password')}
                            onBlur={() => setFocused(null)}
                            style={inputStyle(focused === 'password', !!error)}
                            required
                            data-testid="auth-password-input"
                          />
                        )}
                        {/* A3: live password rules checklist */}
                        {mode === 'signup' && password.length > 0 && (
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '4px 0' }}
                            data-testid="auth-pw-rules">
                            {[
                              { key: 'length',    ok: pwRules.length,    label: '8+ caracteres' },
                              { key: 'hasDigit',  ok: pwRules.hasDigit,  label: '1 número'       },
                              { key: 'hasLetter', ok: pwRules.hasLetter, label: '1 letra'         },
                            ].map(r => (
                              <span key={r.key} style={{ fontSize: 12, fontFamily: 'var(--font-sans)',
                                color: r.ok ? 'var(--pl-sage)' : 'var(--text-muted)', display: 'flex', gap: 4, alignItems: 'center' }}>
                                {r.ok ? '✓' : '○'} {r.label}
                              </span>
                            ))}
                          </div>
                        )}
                        {mode === 'signup' && (
                          <input
                            type="password"
                            autoComplete="new-password"
                            placeholder="Confirmar contraseña"
                            value={passwordConfirm}
                            onChange={(e) => setPasswordConfirm(e.target.value)}
                            onFocus={() => setFocused('confirm')}
                            onBlur={() => setFocused(null)}
                            style={inputStyle(focused === 'confirm', !!error)}
                            required
                          />
                        )}
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Error genérico */}
                {error && (
                  <div style={{ color: 'var(--pl-danger)', fontSize: 13, marginTop: 4, textAlign: 'center' }}>
                    {error}
                  </div>
                )}

                {/* Email no confirmado — inline warning */}
                {emailNotConfirmed && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                      padding: '12px 14px',
                      background: 'color-mix(in srgb, var(--pl-brass) 10%, var(--bg-soft))',
                      border: '1px solid color-mix(in srgb, var(--pl-brass) 30%, transparent)',
                      borderRadius: 10,
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: 'var(--text-soft)',
                    }}
                  >
                    Tu email aún no está confirmado. Revisa tu bandeja de entrada.{' '}
                    <button
                      type="button"
                      onClick={handleResendFromLogin}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--pl-terracota-700)',
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: 'pointer',
                        padding: 0,
                        fontFamily: 'var(--font-sans)',
                      }}
                    >
                      Reenviar confirmación
                    </button>
                  </motion.div>
                )}

                {!(mode === 'reset' && resetSent) && (
                  <PLButton
                    fullWidth
                    size="lg"
                    type="submit"
                    loading={loading}
                    style={{
                      marginTop: 12,
                      background: 'var(--pl-terracota)',
                      color: 'var(--pl-cream)',
                      height: 56,
                      borderRadius: 12,
                    }}
                  >
                    {mode === 'login' && 'Entrar'}
                    {mode === 'signup' && 'Crear cuenta'}
                    {mode === 'reset' && 'Enviar instrucciones'}
                  </PLButton>
                )}

                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => { setMode('reset'); setError(null); setEmailNotConfirmed(false) }}
                    style={{
                      color: 'var(--pl-terracota-700)',
                      fontSize: 13,
                      fontWeight: 500,
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      marginTop: 8,
                      textAlign: 'center',
                    }}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                )}

                {mode === 'reset' && (
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setResetSent(false); setError(null) }}
                    style={{
                      color: 'var(--text-muted)',
                      fontSize: 13,
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      marginTop: 16,
                      textAlign: 'center',
                    }}
                  >
                    ← Volver al login
                  </button>
                )}
              </motion.form>

              {!IS_SUPABASE_CONFIGURED && (
                <p
                  style={{
                    marginTop: 24,
                    maxWidth: 320,
                    fontSize: 11,
                    lineHeight: 1.5,
                    color: 'var(--text-muted)',
                    textAlign: 'center',
                    letterSpacing: '0.02em',
                  }}
                >
                  Front sin backend configurado: no se pueden crear cuentas ni entrar. Lo que escribas vive solo en este dispositivo.
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {demoModalOpen && <DemoModal open={demoModalOpen} onClose={() => setDemoModalOpen(false)} />}
      </AnimatePresence>
    </div>
  )
}
